import React, { useCallback, useEffect, useRef, useState } from 'react';

import { normalizeReportedCreditDenial } from '../common/credits-backend/denial';
import type { CreditsBannerState } from '../common/credits-ui-state/types';
import { getCreditsUsageUrl } from '../common/credits-usage-url';
import { ErrorWithCause, normalizeErrorCause } from '../common/errors';
import { isCreditsDegradationUiEnabled } from '../common/feature-gates';
import { getStoredSnapshotId } from '../common/session-coordination/snapshot-id';
import type {
	CreditsSessionCoordinator,
	CreditsRefreshStateOptions,
	CreditsSnapshotEvent,
} from '../common/session-coordination/types';
import {
	areCreditsBannerStatesEqual,
	deriveCreditsBannerState,
	deriveCreditsBannerStateChange,
} from '../services/credits-ui-state';
import {
	CreditsRequestProvider,
	useCreditsRequest as useRequestState,
	type CreditsRequestState,
} from './credits-request-context';
import type {
	CreditsErrorHandler,
	CreditsProviderProps,
	CreditsProduct,
	CreditsSessionScope,
	CreditsState,
} from './credits-session-state.types';
import {
	CreditsDenialReporterContext,
	CreditsStateContext,
	EMPTY_CREDITS_STATE,
	useCreditsDenialReporterContext,
	useCreditsStateContext,
	type ReportCreditDenial,
} from './credits-state-context';
import { CreditsUsageContext } from './credits-usage-context';

/** Returns the shared credit request progress and latest outcome. */
export const useCreditsRequest = (): CreditsRequestState => useRequestState();

type CreditsSessionStateResult = {
	creditsState: CreditsState;
	reportCreditDenial: ReportCreditDenial;
};

type UseCreditsSessionStateOptions = {
	enabled: boolean;
	product: CreditsProduct;
	cloudId?: string;
	sessionScope: CreditsSessionScope;
	agentId?: string;
	onError: CreditsErrorHandler | null;
};

type CreditsSessionStartupHandle = {
	cancel: () => void;
};

/** Credits state tagged with the agent it was derived for, so it is never read for another agent. */
type AgentScopedSessionState = {
	agentId: string | undefined;
	creditsState: CreditsState;
	/** Identity of the latest accepted snapshot. */
	snapshotKey: string | null;
	/** Snapshot that a reported denial replaced. Replaying it must not undo the report. */
	supersededSnapshotKey: string | null;
};

const createEmptySessionState = (): CreditsState => EMPTY_CREDITS_STATE;

const createAgentScopedSessionState = (
	agentId: string | undefined,
	creditsState: CreditsState = createEmptySessionState(),
	snapshotKey: string | null = null,
	supersededSnapshotKey: string | null = null,
): AgentScopedSessionState => ({ agentId, creditsState, snapshotKey, supersededSnapshotKey });

const HEALTHY_BANNER_STATE: CreditsBannerState = Object.freeze({
	balanceState: 'healthy',
	depletionBehavior: null,
});

const getSnapshotKey = ({ snapshot }: CreditsSnapshotEvent): string =>
	getStoredSnapshotId(snapshot);

const getBrowserDocument = (): Document | null =>
	typeof globalThis.document === 'undefined' ? null : globalThis.document;

const canObserveVisibility = (browserDocument: Document | null): browserDocument is Document =>
	browserDocument !== null && typeof browserDocument.addEventListener === 'function';

const getProviderError = (cause: unknown, fallbackMessage: string): Error =>
	cause instanceof Error
		? cause
		: new ErrorWithCause(fallbackMessage, {
				cause: normalizeErrorCause(cause),
			});

const getCreditsBannerStateFromEvent = ({
	snapshot: storedSnapshot,
}: CreditsSnapshotEvent): CreditsBannerState => {
	const { snapshot: backendSnapshot } = storedSnapshot;
	return deriveCreditsBannerState(backendSnapshot);
};

const getNextSessionState = (
	currentState: CreditsState,
	nextBannerState: CreditsBannerState,
): CreditsState => {
	if (
		currentState.bannerState !== null &&
		areCreditsBannerStatesEqual(currentState.bannerState, nextBannerState)
	) {
		// BroadcastChannel and follower polling can report the same stored
		// snapshot more than once. Keep the previous state-change object so
		// context consumers do not re-render for duplicate credit state.
		return currentState;
	}

	// Keep the current banner state and its state-change flags derived from the
	// same backend response. Consumers can then use `bannerState` for rendering
	// decisions and `bannerStateChange` for side effects without racing two independent
	// calculations.
	return {
		bannerState: nextBannerState,
		bannerStateChange: deriveCreditsBannerStateChange(currentState.bannerState, nextBannerState),
	};
};

const subscribeToVisibleDocumentRefresh = (
	coordinator: CreditsSessionCoordinator,
	reportError: CreditsErrorHandler,
): (() => void) => {
	const browserDocument = getBrowserDocument();
	if (!canObserveVisibility(browserDocument)) {
		return () => {};
	}

	const handleVisibilityChange = () => {
		if (browserDocument.visibilityState !== 'visible') {
			return;
		}

		// Visibility changes are a lightweight revalidation trigger. The
		// coordinator still decides whether this tab may call the backend or
		// should keep following another tab's stored snapshot.
		void coordinator.requestRefresh().catch((cause) => {
			reportError(getProviderError(cause, 'Credits visibility refresh failed.'), 'request-refresh');
		});
	};

	browserDocument.addEventListener('visibilitychange', handleVisibilityChange);

	return () => {
		browserDocument.removeEventListener('visibilitychange', handleVisibilityChange);
	};
};

const resetSessionState = (
	setSessionState: React.Dispatch<React.SetStateAction<AgentScopedSessionState>>,
	agentId: string | undefined,
): void => {
	setSessionState((currentState) => {
		if (
			currentState.agentId === agentId &&
			currentState.creditsState.bannerState === null &&
			currentState.creditsState.bannerStateChange === null
		) {
			return currentState;
		}

		return createAgentScopedSessionState(agentId);
	});
};

const startCreditsSession = ({
	product,
	cloudId,
	sessionScope,
	agentId,
	onSnapshot,
	reportError,
}: {
	product: CreditsProduct;
	cloudId?: string;
	sessionScope: CreditsSessionScope;
	agentId?: string;
	onSnapshot: (snapshotEvent: CreditsSnapshotEvent) => void;
	reportError: CreditsErrorHandler;
}): CreditsSessionStartupHandle => {
	let coordinator: CreditsSessionCoordinator | null = null;
	let unsubscribeFromVisibilityRefresh: (() => void) | null = null;
	let isCancelled = false;

	const stopStartedSession = (): void => {
		unsubscribeFromVisibilityRefresh?.();
		unsubscribeFromVisibilityRefresh = null;
		coordinator?.stop();
		coordinator = null;
	};

	void (async () => {
		try {
			// Load the backend client and coordinator only after the provider has a
			// token provider and the feature gate is on. Gate-off consumers can render
			// the provider without loading polling, storage, or Web Lock code.
			// The shared webpack chunk name is intentional: startup always needs both
			// modules together today. Split the chunk only if a future startup path can
			// use one module without the other.
			const [creditsBackendClientController, creditsSessionCoordinatorController] =
				await Promise.all([
					import(
						/* webpackChunkName: "@atlassian/rovo-credits-session-startup" */ '../controllers/credits-backend-client'
					),
					import(
						/* webpackChunkName: "@atlassian/rovo-credits-session-startup" */ '../controllers/credits-session-coordinator'
					),
				]);
			if (isCancelled) {
				return;
			}

			const backendClient = creditsBackendClientController.createCreditsBackendClient({
				product,
				cloudId,
				...(agentId !== undefined ? { agentId } : {}),
			});

			coordinator = creditsSessionCoordinatorController.createCreditsSessionCoordinator({
				refreshCreditsState: (options?: CreditsRefreshStateOptions) =>
					backendClient.getCreditsState(options),
				parseSnapshot: creditsBackendClientController.parseCreditsBackendSnapshot,
				// Blank cloudIds match the backend client, which omits the header for them.
				...(sessionScope === 'site' ? { siteId: cloudId?.trim() ?? '' } : {}),
				...(agentId !== undefined ? { agentId } : {}),
				onSnapshot,
				onError: reportError,
			});
			coordinator.start();
			if (isCancelled) {
				stopStartedSession();
				return;
			}
			unsubscribeFromVisibilityRefresh = subscribeToVisibleDocumentRefresh(
				coordinator,
				reportError,
			);
		} catch (cause) {
			if (!isCancelled) {
				reportError(
					getProviderError(cause, 'Credits session state startup failed.'),
					'session-startup',
				);
			}
			stopStartedSession();
		}
	})();

	return {
		cancel: () => {
			isCancelled = true;
			stopStartedSession();
		},
	};
};

const useCreditsSessionState = ({
	enabled,
	product,
	cloudId,
	sessionScope,
	agentId,
	onError,
}: UseCreditsSessionStateOptions): CreditsSessionStateResult => {
	const onErrorRef = useRef(onError);
	const [sessionState, setSessionState] = useState<AgentScopedSessionState>(() =>
		createAgentScopedSessionState(agentId),
	);
	const shouldStartSession = enabled;
	const reportTargetRef = useRef({ shouldStartSession, agentId });

	useEffect(() => {
		onErrorRef.current = onError;
	}, [onError]);

	useEffect(() => {
		reportTargetRef.current = { shouldStartSession, agentId };
	}, [shouldStartSession, agentId]);

	useEffect(() => {
		// A new start attempt begins a new state-change history. When startup is
		// disabled, this also clears stale warning/depleted state from consumers.
		resetSessionState(setSessionState, agentId);

		if (!shouldStartSession) {
			return;
		}

		const reportError: CreditsErrorHandler = (error, context) => {
			const onErrorHandler = onErrorRef.current;
			if (onErrorHandler !== null) {
				onErrorHandler(error, context);
			}
		};

		const sessionStartup = startCreditsSession({
			product,
			cloudId,
			sessionScope,
			agentId,
			onSnapshot: (snapshotEvent) => {
				setSessionState((currentState) => {
					const snapshotKey = getSnapshotKey(snapshotEvent);
					if (
						snapshotEvent.source === 'storage' &&
						currentState.agentId === agentId &&
						currentState.supersededSnapshotKey === snapshotKey
					) {
						// This is a replay of the snapshot a reported denial already replaced, so
						// applying it would undo the report. A newer snapshot has a different key.
						return currentState;
					}

					// A snapshot for this agent never builds on another agent's history.
					const previousCreditsState =
						currentState.agentId === agentId
							? currentState.creditsState
							: createEmptySessionState();
					const nextCreditsState = getNextSessionState(
						previousCreditsState,
						getCreditsBannerStateFromEvent(snapshotEvent),
					);
					if (
						currentState.agentId === agentId &&
						nextCreditsState === previousCreditsState &&
						currentState.snapshotKey === snapshotKey &&
						currentState.supersededSnapshotKey === null
					) {
						return currentState;
					}

					return createAgentScopedSessionState(agentId, nextCreditsState, snapshotKey);
				});
			},
			reportError,
		});

		return () => {
			sessionStartup.cancel();
		};
	}, [shouldStartSession, product, cloudId, sessionScope, agentId]);

	const reportCreditDenial = useCallback<ReportCreditDenial>((reportedDenial) => {
		// Read through a ref so the function keeps one identity when the session starts or the agent changes.
		const { shouldStartSession: isSessionRunning, agentId: reportAgentId } =
			reportTargetRef.current;
		const denial = normalizeReportedCreditDenial(reportedDenial);
		if (!isSessionRunning || denial === null) {
			return;
		}

		setSessionState((currentState) => {
			const previousCreditsState =
				currentState.agentId === reportAgentId
					? currentState.creditsState
					: createEmptySessionState();
			// Before the first snapshot a block is still a newly entered state, not an initial one.
			const nextCreditsState = getNextSessionState(
				previousCreditsState.bannerState === null
					? { ...previousCreditsState, bannerState: HEALTHY_BANNER_STATE }
					: previousCreditsState,
				deriveCreditsBannerState({ denial }),
			);
			if (nextCreditsState === previousCreditsState) {
				return currentState;
			}

			const snapshotKey = currentState.agentId === reportAgentId ? currentState.snapshotKey : null;
			return createAgentScopedSessionState(
				reportAgentId,
				nextCreditsState,
				snapshotKey,
				snapshotKey,
			);
		});
	}, []);

	// The reset effect runs after the render that sees a new agentId, so never
	// hand the previous agent's state to consumers in that render.
	return {
		creditsState:
			sessionState.agentId === agentId ? sessionState.creditsState : createEmptySessionState(),
		reportCreditDenial,
	};
};

const MISSING_PROVIDER_MESSAGE =
	'[@atlassian/rovo-credits] useCreditsState was called without a CreditsProvider above it. ' +
	'Returning empty credits state, so no credits banner or state changes will be reported. ' +
	'Mount CreditsProvider above this component.';

/**
 * Reads the provider-owned credits state. Consumers should mount one
 * `CreditsProvider` above the banner and any side-effect listeners so all
 * consumers observe the same backend response and state-change result.
 *
 * Fails open when no `CreditsProvider` is mounted: it returns the empty
 * credits state instead of throwing, and logs a console error once per mounted
 * consumer.
 */
export const useCreditsState: () => CreditsState = () => {
	const creditsState = useCreditsStateContext();
	const isProviderMissing = creditsState === null;

	useEffect(() => {
		if (isProviderMissing) {
			// eslint-disable-next-line no-console
			console.error(MISSING_PROVIDER_MESSAGE);
		}
	}, [isProviderMissing]);

	return creditsState ?? EMPTY_CREDITS_STATE;
};

const MISSING_PROVIDER_REPORT_MESSAGE =
	'[@atlassian/rovo-credits] useReportCreditDenial was called without a CreditsProvider above it. ' +
	'Reported denials are ignored. Mount CreditsProvider above this component.';

const ignoreCreditDenial: ReportCreditDenial = () => {};

/**
 * Returns a stable function that shows a credit denial from a block signal immediately, without
 * calling the credits backend. Pass the `CreditDenial` the signal carries, as received when AI usage is blocked
 * at time of use.
 *
 * The reported denial only changes this tab's state. It is not stored or shared with other tabs, and
 * it stays until the next backend snapshot (scheduled fetch, or another tab's newer one) replaces it.
 * The function is a no-op while the provider's session is not running (gate off, or Rovo not
 * enabled), and when no `CreditsProvider` is mounted, where it logs a console error once per
 * mounted consumer.
 */
export const useReportCreditDenial: () => ReportCreditDenial = () => {
	const reportCreditDenial = useCreditsDenialReporterContext();
	const isProviderMissing = reportCreditDenial === null;

	useEffect(() => {
		if (isProviderMissing) {
			// eslint-disable-next-line no-console
			console.error(MISSING_PROVIDER_REPORT_MESSAGE);
		}
	}, [isProviderMissing]);

	return reportCreditDenial ?? ignoreCreditDenial;
};

/**
 * Starts the active credits session for a mounted provider subtree.
 *
 * The raw `CreditsStateContext.Provider` only stores a value. This component
 * owns the active backend/coordinator session for that context value.
 * `CreditsProvider` always renders it; `enabled` only decides whether browser
 * coordination starts, so gate-off renders keep the same component shape.
 */
const CreditsProviderSession = ({
	children,
	enabled,
	product,
	cloudId,
	usageEnvironment,
	onError,
	onCreditRequestResult,
	sessionScope,
	agentId,
}: CreditsProviderProps & { enabled: boolean }): React.JSX.Element => {
	const { creditsState, reportCreditDenial } = useCreditsSessionState({
		enabled,
		product,
		cloudId,
		sessionScope: sessionScope ?? 'origin',
		// Blank ids are omitted by the backend client, so they must not scope the
		// session either, or "" and no agent would stop sharing state.
		agentId: agentId?.trim() || undefined,
		onError: onError ?? null,
	});

	return React.createElement(
		CreditsUsageContext.Provider,
		{ value: getCreditsUsageUrl(cloudId, usageEnvironment) },
		React.createElement(
			CreditsDenialReporterContext.Provider,
			{ value: reportCreditDenial },
			React.createElement(
				CreditsStateContext.Provider,
				{ value: creditsState },
				React.createElement(CreditsRequestProvider, {
					product,
					cloudId,
					agentId,
					enabled,
					state: creditsState.bannerState,
					onResult: onCreditRequestResult,
					children,
				}),
			),
		),
	);
};

/**
 * Owns browser session state for a subtree. Mount once around the chat
 * surface so `CreditsBanner` and `useCreditsState` consumers share the same
 * coordinator, cached backend response, and previous-to-current state comparison.
 */
export const CreditsProvider: (props: CreditsProviderProps) => React.JSX.Element = ({
	children,
	product,
	cloudId,
	usageEnvironment,
	isRovoEnabled = true,
	onError,
	onCreditRequestResult,
	sessionScope,
	agentId,
}: CreditsProviderProps): React.JSX.Element => {
	const [isEnabled, setIsEnabled] = useState(false);

	useEffect(() => {
		// Defer the gate read until after mount so server and client render the
		// same provider tree first. Startup remains disabled until the gate can
		// be evaluated in the browser.
		setIsEnabled(isCreditsDegradationUiEnabled());
	}, []);

	return React.createElement(CreditsProviderSession, {
		children,
		enabled: isEnabled && isRovoEnabled,
		product,
		cloudId,
		usageEnvironment,
		onError,
		onCreditRequestResult,
		sessionScope,
		agentId,
	});
};
